import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  CheckCircle, Loader2, AlertCircle, FileText, Package, 
  DollarSign, ArrowRight, ShieldCheck
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API } from '../App';

const ApproveQuotePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quoteInfo, setQuoteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [approved, setApproved] = useState(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    fetchQuoteInfo();
  }, [token]);

  const fetchQuoteInfo = async () => {
    try {
      const response = await axios.get(`${API}/quote/approve/${token}`);
      
      if (response.data.already_processed) {
        setError(`This quote has already been ${response.data.status}.`);
      } else if (response.data.expired) {
        setError(response.data.message);
      } else {
        setQuoteInfo(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired approval link');
    }
    setLoading(false);
  };

  const handleApprove = async () => {
    setConfirming(true);
    try {
      const response = await axios.post(`${API}/quote/approve/${token}`, {
        confirmed: true
      });
      
      setApproved(true);
      setOrderId(response.data.order_id);
      toast.success('Quote approved! Check your email for the invoice.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve quote');
    }
    setConfirming(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#CCFF00]" />
      </div>
    );
  }

  if (approved) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20">
          <Card className="bg-[#111] border-green-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Quote Approved!</h1>
              <p className="text-white/70 mb-6">
                Thank you for approving your quote. An invoice has been sent to your email with payment instructions.
              </p>
              <div className="bg-[#050505] rounded-lg p-4 mb-6">
                <p className="text-white/50 text-sm">Order Reference</p>
                <p className="text-[#CCFF00] font-bold text-xl">{orderId}</p>
              </div>
              <p className="text-white/50 text-sm">
                Please check your inbox (and spam folder) for the invoice email.
              </p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20">
          <Card className="bg-[#111] border-red-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Unable to Process</h1>
              <p className="text-white/70 mb-6">{error}</p>
              <p className="text-white/50 text-sm">
                Please contact us at{' '}
                <a href="mailto:hello@3dprintbureau.com.au" className="text-[#CCFF00] hover:underline">
                  hello@3dprintbureau.com.au
                </a>
                {' '}if you need assistance.
              </p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const breakdown = quoteInfo?.quote_breakdown || {};
  const items = breakdown.items || [];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 py-12 pt-28">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Approve Your Quote</h1>
          <p className="text-white/60">Review and approve your 3D printing quote</p>
        </div>

        <Card className="bg-[#111] border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#CCFF00]" />
              Quote Details
            </CardTitle>
            <CardDescription className="text-white/50">
              Quote for {quoteInfo?.customer_name} ({quoteInfo?.customer_email})
            </CardDescription>
          </CardHeader>
          <CardContent>
            {items.length > 0 && (
              <div className="space-y-3 mb-6">
                {items.map((item, index) => {
                  const unitPrice = item.unit_price || (item.total_price / Math.max(item.quantity || 1, 1));
                  return (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-white/10">
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-white/40" />
                        <div>
                          <p className="text-white font-medium">{item.name}</p>
                          <p className="text-white/50 text-sm">
                            {item.material} • Qty: {item.quantity}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white/50 text-xs">
                          ${unitPrice?.toFixed(2)} × {item.quantity}
                        </p>
                        <p className="text-white font-medium">${item.total_price?.toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pricing Breakdown */}
            <div className="border-t border-white/10 pt-4 space-y-2">
              <div className="flex justify-between text-white/70">
                <span>Subtotal</span>
                <span>${breakdown.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white/70">
                <span>Delivery</span>
                <span>${breakdown.delivery?.toFixed(2)}</span>
              </div>
            </div>

            <div className="bg-[#050505] rounded-lg p-6 text-center mt-4">
              <p className="text-white/50 text-sm uppercase tracking-wide mb-2">Total Quote</p>
              <p className="text-4xl font-bold text-[#CCFF00]">
                ${quoteInfo?.quoted_total?.toFixed(2)} AUD
              </p>
            </div>
          </CardContent>
        </Card>

        {!showConfirmation ? (
          <Card className="bg-[#111] border-[#CCFF00]/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <ShieldCheck className="w-6 h-6 text-[#CCFF00] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Ready to proceed?</h3>
                  <p className="text-white/60 text-sm">
                    By approving this quote, you'll receive an invoice via email with payment instructions. 
                    Your order will be processed once payment is confirmed.
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => setShowConfirmation(true)}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold py-6 text-lg"
              >
                Approve Quote & Request Invoice
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#111] border-yellow-500/30">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Confirm Approval</h3>
                <p className="text-white/60">
                  Are you sure you want to approve this quote for <span className="text-[#CCFF00] font-bold">${quoteInfo?.quoted_total?.toFixed(2)} AUD</span>?
                </p>
                <p className="text-white/50 text-sm mt-2">
                  An invoice will be sent to {quoteInfo?.customer_email}
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={confirming}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleApprove}
                  className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
                  disabled={confirming}
                >
                  {confirming ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Yes, Approve Quote
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-white/40 text-sm mt-6">
          Questions? Contact us at{' '}
          <a href="mailto:hello@3dprintbureau.com.au" className="text-[#CCFF00] hover:underline">
            hello@3dprintbureau.com.au
          </a>
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default ApproveQuotePage;
